import produce from 'immer';
import type { GeneralID, ECategoryMap } from '@types';
import { $categoryMap, $categoryList, $sceneMap } from '@stores/dataStores';

function getSceneIds({
  categoryId,
  categoryMap,
  currentId,
  ignoreIdSet,
}: {
  categoryId: number;
  categoryMap: ECategoryMap;
  currentId: GeneralID;
  ignoreIdSet?: Set<GeneralID>;
}): GeneralID[] {
  let sceneIds: GeneralID[] = [];
  const categoryItem = categoryMap.get(categoryId);
  if (categoryItem) {
    if (categoryItem.sceneIds.length > 0) {
      sceneIds.push(...categoryItem.sceneIds);
    } else if (categoryItem.categoryIds.length > 0) {
      for (let i = 0; i < categoryItem.categoryIds.length; i++) {
        sceneIds.push(
          ...getSceneIds({
            categoryId: categoryItem.categoryIds[i],
            categoryMap,
            currentId,
            ignoreIdSet,
          })
        );
      }
    }
  }
  if (ignoreIdSet?.size) {
    const set = produce(ignoreIdSet, (draft) => {
      draft?.delete(currentId);
    });
    sceneIds = sceneIds.filter((x) => !set.has(x));
  }
  return sceneIds;
}

type Params = {
  id: GeneralID;
  categoryMap: ECategoryMap;
  categoryList: number[];
  ignoreIdSet?: Set<GeneralID>;
};

function getNextId({
  id,
  categoryList,
  categoryMap,
  ignoreIdSet,
}: Params): GeneralID {
  // 获取所有的场景列表
  const sceneIds: GeneralID[] = [];
  for (let i = 0; i < categoryList.length; i++) {
    sceneIds.push(
      ...getSceneIds({
        categoryId: categoryList[i],
        categoryMap,
        ignoreIdSet,
        currentId: id,
      })
    );
  }
  // 找到传入id在场景列表中的位置
  const index = sceneIds.indexOf(id);
  if (index === -1) {
    throw new Error(`id ${id} not found in scene list`);
  }
  // 如果是最后一个
  if (index === sceneIds.length - 1) {
    return sceneIds[0];
  }
  // 返回下一个场景的id
  return sceneIds[index + 1];
}

function getPrevId({
  id,
  categoryList,
  categoryMap,
  ignoreIdSet,
}: Params): GeneralID {
  // 获取所有的场景列表
  const sceneIds: GeneralID[] = [];
  for (let i = 0; i < categoryList.length; i++) {
    sceneIds.push(
      ...getSceneIds({
        categoryId: categoryList[i],
        categoryMap,
        ignoreIdSet,
        currentId: id,
      })
    );
  }
  // 找到传入id在场景列表中的位置
  const index = sceneIds.indexOf(id);
  if (index === -1) {
    throw new Error(`id ${id} not found in scene list`);
  }
  // 如果是最后一个
  if (index === 0) {
    return sceneIds[sceneIds.length - 1];
  }
  // 返回下一个场景的id
  return sceneIds[index - 1];
}

function getHiddenIdSet() {
  const ids = [...$sceneMap.getState().values()]
    .filter((x) => x.showThumb === 0)
    .map((x) => x.id);
  return new Set(ids);
}

export function getNextSceneId(
  id: GeneralID,
  options: { ignoreHidden?: boolean } = {}
): GeneralID {
  const categoryMap = $categoryMap.getState();
  const categoryList = $categoryList.getState();
  const ignoreIdSet = options.ignoreHidden ? getHiddenIdSet() : undefined;
  return getNextId({
    id,
    categoryList,
    categoryMap,
    ignoreIdSet,
  });
}

export function getPrevSceneId(
  id: GeneralID,
  options: { ignoreHidden?: boolean } = {}
): GeneralID {
  const categoryMap = $categoryMap.getState();
  const categoryList = $categoryList.getState();
  const ignoreIdSet = options.ignoreHidden ? getHiddenIdSet() : undefined;
  return getPrevId({
    id,
    categoryList,
    categoryMap,
    ignoreIdSet,
  });
}
